import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Loader2, Vote, Megaphone, Trophy, ScrollText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const card = "border border-white/10 bg-black/40 p-5 flex flex-col gap-4";

const STATUS_TXT = { registration: ["text-nc-blue border-nc-blue/40", "Candidacy registration open"], voting: ["text-nc-green border-nc-green/40", "Voting open"], closed: ["text-zinc-400 border-white/15", "Closed"] };

/* Owner Deck — Politics: call elections, open voting, close & declare winners.
   Citizens campaign and vote from the in-game City Hall surface (/cef/politics). */
export function PoliticsPanel({ sid }) {
  const [state, setState] = useState(null);
  const [office, setOffice] = useState("Mayor");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/nc/servers/${sid}/politics`).then((r) => setState(r.data)).catch(() => {});
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  const call = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${sid}/elections`, { office });
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const advance = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${sid}/elections/${state.election.id}/advance`);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const el = state?.election;
  const gov = state?.government;
  const [stCls, stLabel] = el ? STATUS_TXT[el.status] || STATUS_TXT.closed : [];

  return (
    <div className="flex flex-col gap-5">
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Landmark className="w-3.5 h-3.5" /> City Hall</p>

        {gov && (
          <p className="font-mono text-[10px] text-zinc-300 flex items-center gap-2"><Trophy className="w-3.5 h-3.5 text-amber-400" /> Current {gov.office}: <span className="text-amber-300">{gov.name}</span> <span className="text-zinc-600">since {String(gov.since || "").slice(0, 10)}</span></p>
        )}

        {el && (
          <div data-testid="politics-active" className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="font-sans font-semibold text-sm text-zinc-100">{el.office} election</p>
              <span className={`font-mono text-[8px] uppercase border px-1.5 py-0.5 ${stCls}`}>{stLabel}</span>
              <span className="font-mono text-[9px] text-zinc-500 ml-auto">{state.candidates.length} candidate(s) · {el.total_votes} votes</span>
            </div>
            {state.candidates.map((c) => (
              <div key={c.id} className="flex items-center gap-3 font-mono text-[10px] text-zinc-300 border-b border-white/5 pb-1.5">
                <span>{c.name}</span>
                <span className="text-zinc-500 truncate flex-1">{c.slogan}</span>
                <span className="text-nc-orange">{c.votes}</span>
              </div>
            ))}
            <button data-testid="politics-advance" disabled={busy} onClick={advance} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors w-fit flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Vote className="w-4 h-4" />}
              {el.status === "registration" ? "Open voting" : "Close & declare winner"}
            </button>
          </div>
        )}

        {!el && (
          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-56"><span className={labelCls}>Office up for election</span><input data-testid="politics-office" className={inputCls} maxLength={40} value={office} onChange={(e) => setOffice(e.target.value)} /></div>
            <button data-testid="politics-call" disabled={busy || office.trim().length < 3} onClick={call} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 transition-colors flex items-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Call election
            </button>
          </div>
        )}

        {(state?.past || []).length > 0 && (
          <div className="border-t border-white/10 pt-3 flex flex-col gap-1.5">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1.5"><ScrollText className="w-3 h-3" /> Past elections</p>
            {state.past.map((p) => (
              <p key={p.id} className="font-mono text-[9px] text-zinc-500">{String(p.closed_at || "").slice(0, 10)} — {p.office}: <span className="text-zinc-300">{(p.winner || {}).name || "no winner"}</span> ({p.total_votes} votes)</p>
            ))}
          </div>
        )}
        <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">Citizens campaign and vote in-game at <code>/cef/politics/{sid}?token=&lt;session&gt;</code> — one citizen, one vote per election.</p>
      </div>
    </div>
  );
}
